import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Amplify } from 'aws-amplify';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { awsConfig } from './aws-config';
import { Sidebar } from './components/Sidebar';
import { HomePage } from './components/HomePage';
import { HottestChains } from './components/HottestChains';
import { OptionFlowFeed } from './components/OptionFlowFeed';
import { OptionFilterFlow } from './components/OptionFilterFlow';
import { OptionHistoricData } from './components/OptionHistoricData';
import { UnusualFlowsComponent } from './components/UnusualFlowsComponent';
import { Institutions } from './components/Institutions';
import { News } from './components/News';
import './styles/main.css';

// Configure AWS Amplify
Amplify.configure(awsConfig);

function App() {
  // Restore active menu from localStorage or default to 'home'
  const [activeMenu, setActiveMenu] = useState(() => {
    return localStorage.getItem('activeMenu') || 'home';
  });

  // On mobile/tablet (<=1024px), sidebar should be closed by default
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    return window.innerWidth > 1024;
  });
  
  const [isLoaded, setIsLoaded] = useState(false);

  // Save active menu to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('activeMenu', activeMenu);
  }, [activeMenu]);

  // Handle window resize - only auto-close on mobile/tablet, don't auto-open
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024 && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  useEffect(() => {
    // Add loaded class after component mounts to prevent flash
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const renderContent = () => {
    switch (activeMenu) {
      case 'home':
        return <HomePage />;
      case 'hottest-chains':
        return <HottestChains />;
      case 'option-flow':
        return <OptionFlowFeed />;
      case 'option-filter-flow':
        return <OptionFilterFlow />;
      case 'option-historic':
        return <OptionHistoricData />;
      case 'unusual-flows':
        return <UnusualFlowsComponent />;
      case 'institutions':
        return <Institutions />;
      case 'news':
        return <News />;
      default:
        return <HomePage />;
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const contentVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };

  return (
    <AuthProvider>
      <ProtectedRoute>
        <motion.div 
          className={`app-container ${isLoaded ? 'loaded' : ''}`}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        >
          {/* Mobile Overlay */}
          <AnimatePresence>
            {sidebarOpen && (
              <motion.div 
                className="sidebar-overlay active"
                onClick={() => setSidebarOpen(false)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              />
            )}
          </AnimatePresence>

          {/* Sidebar */}
          <Sidebar 
            activeMenu={activeMenu} 
            onMenuClick={(menu) => {
              setActiveMenu(menu);
              // Close sidebar on mobile/tablet after menu selection
              if (window.innerWidth <= 1024) {
                setSidebarOpen(false);
              }
            }}
            isOpen={sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
          />

          {/* Main Content */}
          <motion.div 
            className={`app-main-content ${sidebarOpen ? '' : 'sidebar-closed'}`}
            layout
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          >
            {/* Header */}
            <header className="header">
              <div className="header-container">
                {/* Mobile Hamburger Button */}
                <button
                  className="mobile-hamburger"
                  onClick={() => setSidebarOpen(true)}
                  aria-label="Open menu"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <div className="logo-container">
                  {/* Chart Icon */}
                  <div className="relative">
                    <svg className="logo-icon" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 18h4v-4h4v-4h4V6h4V4" stroke="currentColor" strokeWidth="2" fill="none"/>
                      <rect x="2" y="14" width="1" height="4" fill="currentColor"/>
                      <rect x="6" y="10" width="1" height="8" fill="currentColor"/>
                      <rect x="10" y="6" width="1" height="12" fill="currentColor"/>
                      <rect x="14" y="2" width="1" height="16" fill="currentColor"/>
                      <rect x="18" y="0" width="1" height="18" fill="currentColor"/>
                      <path d="M20 2l2 2-2 2" stroke="currentColor" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                  {/* MIL Text */}
                  <div>
                    <div className="logo-text">MIL</div>
                    <div className="logo-subtitle">
                      MARKET<br/>
                      INTELLIGENCE<br/>
                      LABS
                    </div>
                  </div>
                </div>
                <div className="header-info">
                  <div className="header-info-primary">Market Data & Analysis</div>
                  <div className="header-info-secondary">Educational & Research Tool</div>
                </div>
              </div>
            </header>

            {/* Main Content */}
            <main className="main-content">
              <div className="content-grid">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeMenu}
                    variants={contentVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  >
                    {renderContent()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </main>

            {/* Footer */}
            <footer className="footer">
              <div className="footer-container">
                <p className="footer-text">
                  © 2025 Market Intelligence Labs. Educational market data platform for research and analysis.
                </p>
                <p className="footer-disclaimer">
                  <strong>Disclaimer:</strong> This platform is for educational and research purposes only. 
                  Not financial advice. Always consult with qualified financial professionals before making investment decisions.
                </p>
                <div className="footer-links">
                  <a 
                    href="https://marketintelligencelabs.com/health" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="footer-link"
                  >
                    Health Check
                  </a>
                </div>
              </div>
            </footer>
          </motion.div>
        </motion.div>
      </ProtectedRoute>
    </AuthProvider>
  );
}

export default App;
